// CashflowCalculator.tsx
// Gross-Net Calculator for Property Investment Tracking
// With German/English translation support

import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Calendar, Home,
  Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Clock, PieChart, BarChart3,
  FileText, Info, Calculator
} from 'lucide-react';
import { useLanguage } from './LanguageContext';

// ============================================
// TYPES
// ============================================

interface ExpenseItem {
  id: string;
  category: string;
  description: string;
  amount: number;
  frequency: 'one-time' | 'monthly' | 'quarterly' | 'annual';
  dueDate?: string;
  status: 'paid' | 'pending' | 'overdue';
}

interface IncomeItem {
  id: string;
  type: string;
  description: string;
  amount: number;
  frequency: 'monthly' | 'quarterly' | 'annual';
  startDate?: string;
  status: 'received' | 'expected' | 'overdue';
}

interface PropertyCashflow {
  unitId: string;
  unitName: string;
  year: number;
  purchasePrice: number;
  currentValue: number;
  incomes: IncomeItem[];
  expenses: ExpenseItem[];
  handoverDate?: string;
  constructionProgress?: number;
  taxInfo: {
    corporateTaxApplicable: boolean;
    vatApplicable: boolean;
    exemptionStatus: string;
    notes: string;
  };
}

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatAED = (amount: number) => {
  return `AED ${amount.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const calculateAnnualAmount = (amount: number, frequency: string): number => {
  switch (frequency) {
    case 'monthly': return amount * 12;
    case 'quarterly': return amount * 4;
    case 'annual': return amount;
    case 'one-time': return amount;
    default: return amount;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'paid':
    case 'received': return 'bg-green-100 text-green-700';
    case 'pending':
    case 'expected': return 'bg-yellow-100 text-yellow-700';
    case 'overdue': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'paid':
    case 'received': return <CheckCircle className="h-4 w-4" />;
    case 'pending':
    case 'expected': return <Clock className="h-4 w-4" />;
    case 'overdue': return <AlertCircle className="h-4 w-4" />;
    default: return null;
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const CASHFLOW_COMING_SOON = true;

interface CashflowCalculatorProps {
  investorId?: string;
  units: any[];
  currentInvestor?: any;
  backendUrl?: string;
}

const CashflowCalculator: React.FC<CashflowCalculatorProps> = ({
  units,
  currentInvestor,
  backendUrl
}) => {
  const { t, language } = useLanguage();  // ✅ Added translation hook

  // Expense & Income Categories with translations
  const EXPENSE_CATEGORIES = [
    { value: 'service-charges', label: t('serviceCharges'), icon: '' },
    { value: 'dewa', label: t('dewa'), icon: '' },
    { value: 'property-management', label: t('propertyManagement'), icon: '' },
    { value: 'maintenance', label: t('maintenance'), icon: '' },
    { value: 'insurance', label: t('insurance'), icon: '' },
    { value: 'mortgage', label: t('mortgagePayment'), icon: '' },
    { value: 'furnishing', label: t('furnishing'), icon: '' },
    { value: 'agency-fees', label: t('agencyFees'), icon: '' },
    { value: 'legal', label: t('legalFees'), icon: '' },
    { value: 'other', label: t('other'), icon: '' },
  ];

  const INCOME_TYPES = [
    { value: 'rental', label: t('rentalIncome'), icon: '' },
    { value: 'airbnb', label: t('airbnbIncome'), icon: '' },
    { value: 'appreciation', label: t('expectedAppreciation'), icon: '' },
    { value: 'other', label: t('otherIncome'), icon: '' },
  ];

  // State
  const [selectedUnit, setSelectedUnit] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [cashflowData, setCashflowData] = useState<Record<string, PropertyCashflow>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['summary', 'expenses', 'income']));

  // New expense form
  const [newExpense, setNewExpense] = useState<Partial<ExpenseItem>>({
    category: 'service-charges',
    description: '',
    amount: 0,
    frequency: 'annual',
    status: 'pending'
  });

  // New income form
  const [newIncome, setNewIncome] = useState<Partial<IncomeItem>>({
    type: 'rental',
    description: '',
    amount: 0,
    frequency: 'monthly',
    status: 'expected'
  });

  // Initialize cashflow data
  useEffect(() => {
    const initialData: Record<string, PropertyCashflow> = {};
    
    units.forEach(unit => {
      initialData[unit.id] = {
        unitId: unit.id,
        unitName: unit.name || unit.project || `Unit ${unit.unitNumber}`,
        year: selectedYear,
        purchasePrice: unit.purchaseValue || 0,
        currentValue: unit.currentValue || 0,
        incomes: [],
        expenses: [],
        taxInfo: {
          corporateTaxApplicable: false,
          vatApplicable: false,
          exemptionStatus: t('notApplied'),
          notes: ''
        }
      };
    });

    setCashflowData(initialData);
  }, [units, selectedYear]);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Add expense
  const handleAddExpense = () => {
    if (!selectedUnit || selectedUnit === 'all') {
      alert(t('pleaseSelectProperty'));
      return;
    }
    if (!newExpense.amount) {
      alert(t('pleaseEnterAmount'));
      return;
    }

    const expense: ExpenseItem = {
      id: `exp-${Date.now()}`,
      category: newExpense.category || 'other',
      description: newExpense.description || '',
      amount: newExpense.amount || 0,
      frequency: newExpense.frequency || 'annual',
      status: newExpense.status || 'pending',
      dueDate: newExpense.dueDate
    };

    setCashflowData(prev => ({
      ...prev,
      [selectedUnit]: {
        ...prev[selectedUnit],
        expenses: [...(prev[selectedUnit]?.expenses || []), expense]
      }
    }));

    setNewExpense({
      category: 'service-charges',
      description: '',
      amount: 0,
      frequency: 'annual',
      status: 'pending'
    });
    setShowAddExpense(false);
  };

  // Add income
  const handleAddIncome = () => {
    if (!selectedUnit || selectedUnit === 'all') {
      alert(t('pleaseSelectProperty'));
      return;
    }
    if (!newIncome.amount) {
      alert(t('pleaseEnterAmount'));
      return;
    }

    const income: IncomeItem = {
      id: `inc-${Date.now()}`,
      type: newIncome.type || 'rental',
      description: newIncome.description || '',
      amount: newIncome.amount || 0,
      frequency: newIncome.frequency || 'monthly',
      status: newIncome.status || 'expected'
    };

    setCashflowData(prev => ({
      ...prev,
      [selectedUnit]: {
        ...prev[selectedUnit],
        incomes: [...(prev[selectedUnit]?.incomes || []), income]
      }
    }));

    setNewIncome({
      type: 'rental',
      description: '',
      amount: 0,
      frequency: 'monthly',
      status: 'expected'
    });
    setShowAddIncome(false);
  };

  // Delete expense
  const deleteExpense = (unitId: string, expenseId: string) => {
    setCashflowData(prev => ({
      ...prev,
      [unitId]: {
        ...prev[unitId],
        expenses: prev[unitId].expenses.filter(e => e.id !== expenseId)
      }
    }));
  };

  // Delete income
  const deleteIncome = (unitId: string, incomeId: string) => {
    setCashflowData(prev => ({
      ...prev,
      [unitId]: {
        ...prev[unitId],
        incomes: prev[unitId].incomes.filter(i => i.id !== incomeId)
      }
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    const unitsToCalculate = selectedUnit === 'all' 
      ? Object.values(cashflowData) 
      : [cashflowData[selectedUnit]].filter(Boolean);

    let totalIncome = 0;
    let totalExpenses = 0;
    let totalPurchaseValue = 0;
    let totalCurrentValue = 0;

    unitsToCalculate.forEach(data => {
      if (!data) return;
      
      totalPurchaseValue += data.purchasePrice;
      totalCurrentValue += data.currentValue;
      
      data.incomes.forEach(income => {
        totalIncome += calculateAnnualAmount(income.amount, income.frequency);
      });
      
      data.expenses.forEach(expense => {
        totalExpenses += calculateAnnualAmount(expense.amount, expense.frequency);
      });
    });

    const netCashflow = totalIncome - totalExpenses;
    const appreciation = totalCurrentValue - totalPurchaseValue;
    const appreciationPercent = totalPurchaseValue > 0 
      ? ((appreciation / totalPurchaseValue) * 100).toFixed(1) 
      : '0.0';
    const roi = totalPurchaseValue > 0 
      ? (((netCashflow + appreciation) / totalPurchaseValue) * 100).toFixed(1) 
      : '0.0';

    return {
      totalIncome,
      totalExpenses,
      netCashflow,
      totalPurchaseValue,
      totalCurrentValue,
      appreciation,
      appreciationPercent,
      roi,
      unitCount: unitsToCalculate.length
    };
  };

  const totals = calculateTotals();

  // Parked until it reads real income and expenses: it showed AED 0 everywhere
  // next to a portfolio-wide value loss. Flip to false to bring it back.
  if (CASHFLOW_COMING_SOON) {
    return (
      <div className="cashflow-calculator" style={{ padding: '16px', paddingBottom: '100px', background: '#f5f5f5', minHeight: '100vh' }}>
        <div style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '3px solid #D4AF37' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Calculator className="h-6 w-6" style={{ color: '#D4AF37' }} />
            {t('cashflowCalculator')}
          </h1>
          <p style={{ color: '#666', fontSize: '0.85rem' }}>{t('grossNetAnalysis')}</p>
        </div>
        <div style={{ background: '#fff', borderRadius: '12px', padding: '40px 20px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <Calculator style={{ width: 44, height: 44, color: '#D4AF37', margin: '0 auto 14px' }} />
          <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e' }}>{t('comingSoon')}</div>
          <p style={{ color: '#666', fontSize: '0.95rem', maxWidth: 420, margin: '10px auto 0', lineHeight: 1.5 }}>
            {language === 'de'
              ? 'Ihre Brutto-Netto-Analyse mit echten Mieteinnahmen und Ausgaben ist bald hier verfügbar.'
              : 'Your gross-to-net analysis with real rental income and expenses will be available here soon.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cashflow-calculator" style={{ padding: '16px', paddingBottom: '100px', background: '#f5f5f5', minHeight: '100vh' }}>
      
      {/* Header */}
      <div style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '3px solid #D4AF37' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Calculator className="h-6 w-6" style={{ color: '#D4AF37' }} />
          {t('cashflowCalculator')}
        </h1>
        <p style={{ color: '#666', fontSize: '0.85rem' }}>{t('grossNetAnalysis')}</p>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#666', marginBottom: '6px', textTransform: 'uppercase' }}>
              {t('property')}
            </label>
            <select
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #D4AF37', borderRadius: '8px', background: '#fff', fontWeight: '500' }}
            >
              <option value="all">{t('allProperties')} ({units.length})</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>
                  {unit.name || unit.project} - {unit.unitNumber}
                </option>
              ))}
            </select>
          </div>
          
          <div style={{ minWidth: '120px' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#666', marginBottom: '6px', textTransform: 'uppercase' }}>
              {t('year')}
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e5e5', borderRadius: '8px', background: '#fff', fontWeight: '500' }}
            >
              {[2024, 2025, 2026, 2027, 2028].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '2px solid #e5e5e5' }}>
          <p style={{ fontSize: '0.65rem', fontWeight: '600', color: '#888', textTransform: 'uppercase', marginBottom: '4px' }}>{t('totalIncome')}</p>
          <p style={{ fontSize: '1.1rem', fontWeight: '700', color: '#22c55e' }}>{formatAED(totals.totalIncome)}</p>
          <p style={{ fontSize: '0.6rem', color: '#999' }}>{t('annual')}</p>
        </div>
        
        <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '2px solid #e5e5e5' }}>
          <p style={{ fontSize: '0.65rem', fontWeight: '600', color: '#888', textTransform: 'uppercase', marginBottom: '4px' }}>{t('totalExpenses')}</p>
          <p style={{ fontSize: '1.1rem', fontWeight: '700', color: '#ef4444' }}>{formatAED(totals.totalExpenses)}</p>
          <p style={{ fontSize: '0.6rem', color: '#999' }}>{t('annual')}</p>
        </div>
        
        <div style={{ background: '#1a1a2e', padding: '16px', borderRadius: '12px', border: '2px solid #D4AF37' }}>
          <p style={{ fontSize: '0.65rem', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', marginBottom: '4px' }}>{t('netCashflow')}</p>
          <p style={{ fontSize: '1.2rem', fontWeight: '700', color: totals.netCashflow >= 0 ? '#22c55e' : '#ef4444' }}>
            {formatAED(totals.netCashflow)}
          </p>
          <p style={{ fontSize: '0.6rem', color: '#777' }}>{t('annual')}</p>
        </div>
        
        <div style={{ background: '#1a1a2e', padding: '16px', borderRadius: '12px', border: '2px solid #D4AF37' }}>
          <p style={{ fontSize: '0.65rem', fontWeight: '600', color: '#aaa', textTransform: 'uppercase', marginBottom: '4px' }}>{t('totalRoi')}</p>
          <p style={{ fontSize: '1.2rem', fontWeight: '700', color: '#D4AF37' }}>{totals.roi}%</p>
          <p style={{ fontSize: '0.6rem', color: '#777' }}>{t('includingAppreciation')}</p>
        </div>
      </div>

      {/* Appreciation Card */}
      <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', marginBottom: '16px', border: '2px solid #e5e5e5' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#666', textTransform: 'uppercase' }}>{t('propertyAppreciation')}</p>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: totals.appreciation >= 0 ? '#22c55e' : '#ef4444' }}>
              {formatAED(totals.appreciation)}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600', background: totals.appreciation >= 0 ? '#dcfce7' : '#fee2e2', color: totals.appreciation >= 0 ? '#16a34a' : '#dc2626' }}>
              {totals.appreciation >= 0 ? '+' : ''}{totals.appreciationPercent}%
            </span>
          </div>
        </div>
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#666' }}>
          <span>{t('purchaseValue')}: {formatAED(totals.totalPurchaseValue)}</span>
          <span>{t('currentValue')}: {formatAED(totals.totalCurrentValue)}</span>
        </div>
      </div>

      {/* Expenses Section */}
      <div style={{ background: '#fff', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <button
          onClick={() => toggleSection('expenses')}
          style={{ width: '100%', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <TrendingDown className="h-5 w-5" style={{ color: '#ef4444' }} />
            <span style={{ fontWeight: '600', color: '#1a1a2e' }}>{t('expenses')}</span>
          </div>
          {expandedSections.has('expenses') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {expandedSections.has('expenses') && (
          <div style={{ padding: '0 16px 16px' }}>
            {selectedUnit !== 'all' && cashflowData[selectedUnit]?.expenses.map(expense => (
              <div key={expense.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '8px' }}>
                <div>
                  <p style={{ fontWeight: '600', color: '#1a1a2e' }}>
                    {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.icon} {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label}
                  </p>
                  {expense.description && <p style={{ fontSize: '0.8rem', color: '#666' }}>{expense.description}</p>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontWeight: '600', color: '#ef4444' }}>{formatAED(expense.amount)}</span>
                  <button onClick={() => deleteExpense(selectedUnit, expense.id)} style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <Trash2 size={16} style={{ color: '#999' }} />
                  </button>
                </div>
              </div>
            ))}

            {selectedUnit !== 'all' && (
              <button
                onClick={() => setShowAddExpense(true)}
                style={{ width: '100%', padding: '12px', border: '2px dashed #D4AF37', borderRadius: '8px', background: 'transparent', color: '#D4AF37', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <Plus size={18} /> {t('addExpense')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Income Section */}
      <div style={{ background: '#fff', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <button
          onClick={() => toggleSection('income')}
          style={{ width: '100%', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <TrendingUp className="h-5 w-5" style={{ color: '#22c55e' }} />
            <span style={{ fontWeight: '600', color: '#1a1a2e' }}>{t('income')}</span>
          </div>
          {expandedSections.has('income') ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {expandedSections.has('income') && (
          <div style={{ padding: '0 16px 16px' }}>
            {selectedUnit !== 'all' && cashflowData[selectedUnit]?.incomes.map(income => (
              <div key={income.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '8px' }}>
                <div>
                  <p style={{ fontWeight: '600', color: '#1a1a2e' }}>
                    {INCOME_TYPES.find(i => i.value === income.type)?.icon} {INCOME_TYPES.find(i => i.value === income.type)?.label}
                  </p>
                  {income.description && <p style={{ fontSize: '0.8rem', color: '#666' }}>{income.description}</p>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontWeight: '600', color: '#22c55e' }}>{formatAED(income.amount)}</span>
                  <button onClick={() => deleteIncome(selectedUnit, income.id)} style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <Trash2 size={16} style={{ color: '#999' }} />
                  </button>
                </div>
              </div>
            ))}

            {selectedUnit !== 'all' && (
              <button
                onClick={() => setShowAddIncome(true)}
                style={{ width: '100%', padding: '12px', border: '2px dashed #22c55e', borderRadius: '8px', background: 'transparent', color: '#22c55e', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <Plus size={18} /> {t('addIncome')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add Expense Modal */}
      {showAddExpense && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontWeight: '600' }}>{t('addExpense')}</h3>
              <button onClick={() => setShowAddExpense(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '6px' }}>{t('category')}</label>
                <select
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}
                >
                  {EXPENSE_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '6px' }}>{t('amount')} (AED)</label>
                <input
                  type="number"
                  value={newExpense.amount || ''}
                  onChange={(e) => setNewExpense({...newExpense, amount: Number(e.target.value)})}
                  placeholder={t('enterAmount')}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '6px' }}>{t('frequency')}</label>
                <select
                  value={newExpense.frequency}
                  onChange={(e) => setNewExpense({...newExpense, frequency: e.target.value as any})}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}
                >
                  <option value="one-time">{t('oneTime')}</option>
                  <option value="monthly">{t('monthly')}</option>
                  <option value="quarterly">{t('quarterly')}</option>
                  <option value="annual">{t('annual')}</option>
                </select>
              </div>
              <button
                onClick={handleAddExpense}
                style={{ width: '100%', padding: '14px', background: '#D4AF37', color: '#000', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}
              >
                {t('addExpense')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Income Modal */}
      {showAddIncome && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontWeight: '600' }}>{t('addIncome')}</h3>
              <button onClick={() => setShowAddIncome(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '6px' }}>{t('incomeType')}</label>
                <select
                  value={newIncome.type}
                  onChange={(e) => setNewIncome({...newIncome, type: e.target.value})}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}
                >
                  {INCOME_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.icon} {type.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '6px' }}>{t('amount')} (AED)</label>
                <input
                  type="number"
                  value={newIncome.amount || ''}
                  onChange={(e) => setNewIncome({...newIncome, amount: Number(e.target.value)})}
                  placeholder={t('enterAmount')}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', marginBottom: '6px' }}>{t('frequency')}</label>
                <select
                  value={newIncome.frequency}
                  onChange={(e) => setNewIncome({...newIncome, frequency: e.target.value as any})}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' }}
                >
                  <option value="monthly">{t('monthly')}</option>
                  <option value="quarterly">{t('quarterly')}</option>
                  <option value="annual">{t('annual')}</option>
                </select>
              </div>
              <button
                onClick={handleAddIncome}
                style={{ width: '100%', padding: '14px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}
              >
                {t('addIncome')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashflowCalculator;